import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import type { Connection, TransactionInstruction } from '@solana/web3.js';
import { type PublicKey, SystemProgram } from '@solana/web3.js';
import type BN from 'bn.js';
import { getProgram } from '../idl/loader.js';
import { deriveEventAuthorityPda, deriveGiftAdminPda, deriveGiftPda } from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, invokeMethod } from './common.js';

/** Mirror of the on-chain `GiftKind` Anchor enum tag mapping. */
export type GiftKindVariant = 'bondingCurveToken' | 'prizePoolShare' | 'usdc';

function giftKindToAnchorVariant(kind: GiftKindVariant): Record<string, Record<string, never>> {
  // Anchor enums serialise as `{ variantName: {} }` in JS.
  return { [kind]: {} };
}

export interface CreateGiftParams extends BuildParams {
  /** GiftConfig PDA (looked up server-side from deployer+label). */
  giftConfig: PublicKey;
  /** Off-chain handler hash — SHA-256 of the canonical handler string. */
  handlerHash: Uint8Array;
  /** Gift kind (drives downstream indexer settlement). */
  kind: GiftKindVariant;
  /** Token amount in mint base units. D6 for USDC, raw for outcome tokens. */
  amount: BN;
  /** Mint of the gifted token. */
  mint: PublicKey;
  /** Backend authority pubkey (signer on the ix; pays init rent). */
  backendAuthority: PublicKey;
}

/**
 * Build the `create_gift` instruction. Backend-only — only
 * `backend_authority` is required as signer; the source treasury
 * funding is done out-of-band via plain SPL transfer to the GiftAdmin
 * ATA before / after this instruction.
 */
export async function buildCreateGift(
  connection: Connection,
  params: CreateGiftParams,
): Promise<BuildResult> {
  if (params.handlerHash.length !== 32) {
    throw new Error('buildCreateGift: handler_hash must be 32 bytes');
  }
  const program = await getProgram(connection, params.programId);
  const [giftPda] = deriveGiftPda(
    params.giftConfig,
    params.mint,
    params.handlerHash,
    program.programId,
  );
  const [eventAuthority] = deriveEventAuthorityPda(program.programId);

  // Anchor's typed method invocation. Args order matches the IDL:
  // `create_gift(handler_hash: [u8;32], kind: GiftKind, amount: u64)`.
  const ix: TransactionInstruction = await invokeMethod(
    program,
    'createGift',
    Array.from(params.handlerHash),
    giftKindToAnchorVariant(params.kind),
    params.amount,
  )
    .accounts({
      backendAuthority: params.backendAuthority,
      giftConfig: params.giftConfig,
      mint: params.mint,
      gift: giftPda,
      systemProgram: SystemProgram.programId,
      eventAuthority,
      program: program.programId,
    })
    .instruction();

  return createBuildResult([ix], [params.backendAuthority], 80_000);
}

export interface ClaimGiftParams extends BuildParams {
  giftConfig: PublicKey;
  handlerHash: Uint8Array;
  mint: PublicKey;
  /** Recipient Turnkey-controlled wallet — signer on the ix. */
  recipient: PublicKey;
  /** Backend authority — also a signer. Pays the recipient ATA init rent. */
  backendAuthority: PublicKey;
}

/**
 * Build the `claim_gift` instruction. DUAL-SIGNER: both
 * `backend_authority` and `recipient` are required signers. The
 * recipient ATA is initialised via `init_if_needed` (rent paid by
 * backend); the source treasury → recipient ATA transfer is signed by
 * the GiftAdmin PDA via the markets program's `invoke_signed` path.
 */
export async function buildClaimGift(
  connection: Connection,
  params: ClaimGiftParams,
): Promise<BuildResult> {
  if (params.handlerHash.length !== 32) {
    throw new Error('buildClaimGift: handler_hash must be 32 bytes');
  }
  const program = await getProgram(connection, params.programId);
  const [giftPda] = deriveGiftPda(
    params.giftConfig,
    params.mint,
    params.handlerHash,
    program.programId,
  );
  const [giftAdmin] = deriveGiftAdminPda(params.giftConfig, program.programId);
  const sourceTreasury = getAssociatedTokenAddressSync(
    params.mint,
    giftAdmin,
    /*allowOwnerOffCurve=*/ true,
    TOKEN_PROGRAM_ID,
  );
  const recipientTokenAccount = getAssociatedTokenAddressSync(
    params.mint,
    params.recipient,
    /*allowOwnerOffCurve=*/ false,
    TOKEN_PROGRAM_ID,
  );
  const [eventAuthority] = deriveEventAuthorityPda(program.programId);

  const ix: TransactionInstruction = await invokeMethod(
    program,
    'claimGift',
    Array.from(params.handlerHash),
  )
    .accounts({
      backendAuthority: params.backendAuthority,
      giftConfig: params.giftConfig,
      gift: giftPda,
      mint: params.mint,
      giftAdmin,
      sourceTreasury,
      recipient: params.recipient,
      recipientTokenAccount,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
      eventAuthority,
      program: program.programId,
    })
    .instruction();

  return createBuildResult([ix], [params.backendAuthority, params.recipient], 120_000);
}
